import React, { useState, useEffect } from 'react';
import {
    XMarkIcon,
    ArrowDownTrayIcon,
    DocumentArrowDownIcon,
    PencilIcon,
    LockClosedIcon
} from '@heroicons/react/24/outline';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import {
  submitTimesheet,
  requestTimesheetEdit,
  createTimesheetDraft,
  getTimesheetWeekId
} from "../services/employee.service";

// --- CONSTANTS ---
const CURRENT_EMPLOYEE = {
    id: 'E1001',
    name: 'Arjun Singh',
    designation: 'Sr. Engineer'
};

const MANAGERS = [
    { id: 'M101', name: "Priya Sharma", role: "HR Manager" },
    { id: 'M102', name: "Vikram Kumar", role: "Project Director" },
];

const YEARS = [2024, 2025, 2026];
const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const STATUS_OPTIONS = {
    DRAFT: 'Draft',
    SUBMITTED: 'Submitted',
    APPROVED: 'Approved',
    REJECTED: 'Rejected'
};

// --- UTILITIES ---
const getWeekKey = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
    return `${d.getFullYear()}-W${weekNo.toString().padStart(2, '0')}`;
};

const getDaysInMonth = (year, month) => {
    const date = new Date(year, month, 1);
    const days = [];
    while (date.getMonth() === month) {
        days.push(new Date(date));
        date.setDate(date.getDate() + 1);
    }
    return days;
};

const toISODate = (ddmmyyyy) => {
  const [dd, mm, yyyy] = ddmmyyyy.split("/");
  return `${yyyy}-${mm}-${dd}`;
};

const getWeekStartDate = (weekKey) => {
  const [year, week] = weekKey.split("-W").map(Number);
  const simple = new Date(year, 0, 1 + (week - 1) * 7);
  const dayOfWeek = simple.getDay();
  const monday = new Date(simple);
  monday.setDate(simple.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
  return monday.toISOString().split("T")[0];
};

// --- TIMESHEET MODAL (now the main component) ---
const TimesheetEntryForm = () => {
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [reportingManager, setReportingManager] = useState(MANAGERS[0].id);
    const [logs, setLogs] = useState([]);
    const [weekStatuses, setWeekStatuses] = useState({});
    const [activeTab, setActiveTab] = useState('current');
    const [showEditRequestModal, setShowEditRequestModal] = useState(false);
    const [editReason, setEditReason] = useState('');
    const [editComments, setEditComments] = useState('');

    // Load data on mount or when year/month changes
    useEffect(() => {
        const days = getDaysInMonth(selectedYear, selectedMonth);
        const initialLogs = days.map(d => {
            const dateStr = d.toLocaleDateString('en-GB');
            const dayName = d.toLocaleDateString('en-US', { weekday: 'long' });
            const weekKey = getWeekKey(d);

            return {
                date: dateStr,
                day: dayName,
                weekKey,
                task: '',
                billable: 0,
                overtime: 0,
                sickLeave: 0,
                notes: '',
            };
        });
        setLogs(initialLogs);

        // Initialize all weeks as DRAFT
        const statuses = {};
        days.forEach(d => {
            const wk = getWeekKey(d);
            if (!statuses[wk]) {
                statuses[wk] = { status: STATUS_OPTIONS.DRAFT, rejectedDays: [], comments: '' };
            }
        });
        setWeekStatuses(statuses);
    }, [selectedYear, selectedMonth]);

    const handleLogChange = (index, field, value) => {
        const newLogs = [...logs];
        if (field === 'billable' || field === 'overtime' || field === 'sickLeave') {
            newLogs[index][field] = value === '' ? 0 : parseFloat(value) || 0;
        } else {
            newLogs[index][field] = value;
        }
        setLogs(newLogs);
    };

    const getRowStatus = (log) => {
        const weekInfo = weekStatuses[log.weekKey] || { status: STATUS_OPTIONS.DRAFT };
        if (weekInfo.status === STATUS_OPTIONS.APPROVED) return STATUS_OPTIONS.APPROVED;
        if (weekInfo.status === STATUS_OPTIONS.SUBMITTED) return STATUS_OPTIONS.SUBMITTED;
        if (weekInfo.status === STATUS_OPTIONS.REJECTED && weekInfo.rejectedDays.includes(log.date)) return STATUS_OPTIONS.REJECTED;
        return STATUS_OPTIONS.DRAFT;
    };

    const isRowEditable = (log) => {
        const status = getRowStatus(log);
        return status === STATUS_OPTIONS.DRAFT || status === STATUS_OPTIONS.REJECTED;
    };

    const isWeekSubmittedOrApproved = (weekKey) => {
        const info = weekStatuses[weekKey];
        return info && (info.status === STATUS_OPTIONS.SUBMITTED || info.status === STATUS_OPTIONS.APPROVED);
    };

    const handleSubmitWeek = async (weekKey) => {
        try {
            const weekStart = getWeekStartDate(weekKey);

            console.log("🧡 Creating draft for:", weekStart);
            await createTimesheetDraft(weekStart);

            console.log("🚀 Submitting week:", weekKey);

            await submitTimesheet({
                manager_id: reportingManager,
                year: selectedYear,
                month: selectedMonth + 1,
                week_key: weekKey,
                entries: logs
                    .filter(l => l.weekKey === weekKey)
                    .map(log => ({
                        date: toISODate(log.date),
                        task: log.task,
                        billable_hours: Number(log.billable),
                        overtime_hours: Number(log.overtime),
                        sick_leave: Number(log.sickLeave),
                        notes: log.notes
                    }))
            }, weekStart);

            setWeekStatuses(prev => ({
                ...prev,
                [weekKey]: {
                    ...prev[weekKey],
                    status: STATUS_OPTIONS.SUBMITTED
                }
            }));

            alert("Timesheet submitted successfully");
        } catch (err) {
            console.error(err);
            alert("Failed to submit timesheet");
        }
    };

    const handleEditRequest = async () => {
        if (!editReason.trim()) return;

        try {
            const weekKey = sortedWeeks.find(wk => {
                const info = weekStatuses[wk];
                return info &&
                    (info.status === STATUS_OPTIONS.SUBMITTED ||
                     info.status === STATUS_OPTIONS.APPROVED);
            });

            if (!weekKey) {
                alert("No submitted or approved week found");
                return;
            }

            const weekStart = getWeekStartDate(weekKey);
            console.log("🔍 Fetching week_id for:", weekStart);

            const response = await getTimesheetWeekId(weekStart);
            console.log("🟢 FULL response:", response);

            const week_id = response?.data?.week_id;

            if (!week_id) {
                alert("Unable to find week id");
                return;
            }

            await requestTimesheetEdit(week_id, {
                reason: editReason
            });

            setShowEditRequestModal(false);
            setEditReason('');
            setEditComments('');

            alert("Edit request sent successfully");
        } catch (err) {
            console.error("❌ Edit request error:", err);
            alert("Failed to send edit request");
        }
    };

    const totals = logs.reduce((acc, curr) => ({
        billable: acc.billable + parseFloat(curr.billable || 0),
        overtime: acc.overtime + parseFloat(curr.overtime || 0),
        sickLeave: acc.sickLeave + parseFloat(curr.sickLeave || 0),
    }), { billable: 0, overtime: 0, sickLeave: 0 });

    const weeksGroup = logs.reduce((acc, log) => {
        if (!acc[log.weekKey]) acc[log.weekKey] = [];
        acc[log.weekKey].push(log);
        return acc;
    }, {});

    const sortedWeeks = Object.keys(weeksGroup).sort();

    console.log("DEBUG weekStatuses:", weekStatuses);

    // Check if there is at least one Submitted or Rejected week
    const hasSubmittedOrRejectedWeek = sortedWeeks.some(weekKey => {
        const info = weekStatuses[weekKey];
        return info && (info.status === STATUS_OPTIONS.SUBMITTED || info.status === STATUS_OPTIONS.REJECTED);
    });

    // --- DOWNLOAD HANDLERS ---
    const downloadExcel = () => {
        const data = logs.map(row => ({
            'Date': row.date,
            'Day': row.day,
            'Task Description': row.task,
            'Billable Hours': row.billable,
            'Overtime': row.overtime,
            'Total Hours': (parseFloat(row.billable || 0) + parseFloat(row.overtime || 0)).toFixed(1),
            'Sick Leave': row.sickLeave,
            'Notes/Comments': row.notes,
            'Status': getRowStatus(row)
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Timesheet");
        XLSX.writeFile(wb, `Timesheet_${MONTHS[selectedMonth]}_${selectedYear}.xlsx`);
    };

    const downloadPDF = () => {
        const doc = new jsPDF('l', 'mm', 'a4');
        doc.setFontSize(14);
        doc.text(`Timesheet`, 14, 15);
        doc.setFontSize(10);
        doc.text(`Employee: ${CURRENT_EMPLOYEE.name} | Period: ${MONTHS[selectedMonth]} ${selectedYear}`, 14, 22);

        const tableColumn = ["Date", "Day", "Task Description", "Billable Hours", "Over Time", "Total Hours", "Sick Leave", "Status", "Notes/Comments"];
        const tableRows = logs.map(log => [
            log.date, log.day, log.task, log.billable, log.overtime,
            (parseFloat(log.billable || 0) + parseFloat(log.overtime || 0)).toFixed(1),
            log.sickLeave, getRowStatus(log), log.notes
        ]);

        autoTable(doc, {
            startY: 30,
            head: [tableColumn],
            body: tableRows,
            theme: 'grid',
            headStyles: { fillColor: [31, 41, 55] },
            didParseCell: (data) => {
                if (data.section === 'body' && (data.row.cells[1].raw === 'Saturday' || data.row.cells[1].raw === 'Sunday')) {
                    data.cell.styles.fillColor = [254, 249, 195];
                }
            }
        });
        doc.save(`Timesheet_${MONTHS[selectedMonth]}_${selectedYear}.pdf`);
    };

    // Function to go back to previous page (dashboard)
    const goBack = () => {
        window.history.back();
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-[1000] flex justify-center items-center p-2 font-roboto">
            <style>
                {`
                @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;600&family=Roboto:wght@400;500;700&display=swap');
                .font-poppins { font-family: 'Poppins', sans-serif; }
                .font-roboto { font-family: 'Roboto', sans-serif; }
                .bg-weekend { background-color: #fef9c3 !important; }
                `}
            </style>

            <div className="bg-white w-full max-w-4xl max-h-[96vh] overflow-hidden flex flex-col border border-gray-400 shadow-2xl">
                {/* Modal Header */}
                <div className="px-3 py-2 bg-gray-800 text-white flex justify-between items-center">
                    <h2 className="text-[11px] font-bold font-poppins uppercase tracking-widest text-blue-400">Timesheet Entry Form</h2>
                    <button onClick={goBack} className="text-gray-400 hover:text-white transition">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-3 overflow-y-auto flex-1 space-y-3">
                    {/* Selectors */}
                    <div className="grid grid-cols-4 gap-2 bg-gray-50 p-2 border border-gray-200">
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Select Year</label>
                            <select value={selectedYear} onChange={(e) => setSelectedYear(parseInt(e.target.value))} className="w-full border border-gray-300 p-1 text-xs outline-none font-roboto">
                                {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Select Month</label>
                            <select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))} className="w-full border border-gray-300 p-1 text-xs outline-none font-roboto">
                                {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
                            </select>
                        </div>
                        <div className="col-span-2">
                            <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Reporting Manager</label>
                            <select value={reportingManager} onChange={(e) => setReportingManager(e.target.value)} className="w-full border border-gray-300 p-1 text-xs outline-none font-roboto">
                                {MANAGERS.map(m => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="flex border-b border-gray-300">
                        <button
                            onClick={() => setActiveTab('current')}
                            className={`px-4 py-2 text-[11px] font-bold uppercase ${activeTab === 'current' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
                        >
                            Current / Draft Weeks
                        </button>
                        <button
                            onClick={() => setActiveTab('submitted')}
                            className={`px-4 py-2 text-[11px] font-bold uppercase ${activeTab === 'submitted' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
                        >
                            View Submitted Timesheets
                        </button>
                    </div>

                    {/* Table */}
                    <div className="overflow-x-auto border border-gray-300">
                        <table className="w-full text-left border-collapse min-w-[1100px] font-roboto">
                            <thead className="bg-gray-100 text-gray-600 text-[10px] font-bold uppercase sticky top-0 z-10 shadow-sm">
                                <tr>
                                    <th className="p-1 border-r border-gray-300 w-24">Date</th>
                                    <th className="p-1 border-r border-gray-300 w-24">Day</th>
                                    <th className="p-1 border-r border-gray-300">Task Description</th>
                                    <th className="p-1 border-r border-gray-300 text-center">Billable Hours</th>
                                    <th className="p-1 border-r border-gray-300 text-center">Over Time</th>
                                    <th className="p-1 border-r border-gray-300 text-center">Total Hours</th>
                                    <th className="p-1 border-r border-gray-300 text-center">Sick Leave</th>
                                    <th className="p-1 border-r border-gray-300 text-center">Status</th>
                                    <th className="p-1">Notes/Comments</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedWeeks.map(weekKey => {
                                    const weekLogs = weeksGroup[weekKey];
                                    const weekInfo = weekStatuses[weekKey] || { status: STATUS_OPTIONS.DRAFT };
                                    const isViewOnly = activeTab === 'submitted' || weekInfo.status === STATUS_OPTIONS.APPROVED || weekInfo.status === STATUS_OPTIONS.SUBMITTED;

                                    return (
                                        <React.Fragment key={weekKey}>
                                            {weekLogs.map((log, index) => {
                                                const rowStatus = getRowStatus(log);
                                                const editable = !isViewOnly && isRowEditable(log);
                                                const totalHours = (parseFloat(log.billable || 0) + parseFloat(log.overtime || 0)).toFixed(1);

                                                return (
                                                    <tr key={index} className={`border-b border-gray-200 transition ${log.day === 'Saturday' || log.day === 'Sunday' ? 'bg-weekend' : 'hover:bg-blue-50/30'}`}>
                                                        <td className="p-1 border-r border-gray-300 text-[11px] font-medium">{log.date}</td>
                                                        <td className="p-1 border-r border-gray-300 text-[11px]">{log.day}</td>
                                                        <td className="p-0 border-r border-gray-300">
                                                            <input
                                                                type="text"
                                                                disabled={!editable}
                                                                className="w-full p-1 border-none bg-transparent text-[11px] outline-none disabled:text-gray-600"
                                                                value={log.task}
                                                                onChange={(e) => handleLogChange(logs.findIndex(l => l.date === log.date), 'task', e.target.value)}
                                                            />
                                                        </td>
                                                        <td className="p-0 border-r border-gray-300">
                                                            <input
                                                                type="number"
                                                                disabled={!editable}
                                                                className="w-full p-1 border-none text-center bg-transparent text-[11px] outline-none disabled:text-gray-600"
                                                                value={log.billable}
                                                                onChange={(e) => handleLogChange(logs.findIndex(l => l.date === log.date), 'billable', e.target.value)}
                                                            />
                                                        </td>
                                                        <td className="p-0 border-r border-gray-300">
                                                            <input
                                                                type="number"
                                                                disabled={!editable}
                                                                className="w-full p-1 border-none text-center bg-transparent text-[11px] outline-none text-blue-600 font-bold disabled:text-gray-600"
                                                                value={log.overtime}
                                                                onChange={(e) => handleLogChange(logs.findIndex(l => l.date === log.date), 'overtime', e.target.value)}
                                                            />
                                                        </td>
                                                        <td className="p-1 border-r border-gray-300 text-center text-[11px] font-bold">{totalHours}</td>
                                                        <td className="p-0 border-r border-gray-300">
                                                            <input
                                                                type="number"
                                                                disabled={!editable}
                                                                className="w-full p-1 border-none text-center bg-transparent text-[11px] outline-none text-red-600 disabled:text-gray-600"
                                                                value={log.sickLeave}
                                                                onChange={(e) => handleLogChange(logs.findIndex(l => l.date === log.date), 'sickLeave', e.target.value)}
                                                            />
                                                        </td>
                                                        <td className="p-1 border-r border-gray-300 text-center text-[11px] font-medium">
                                                            <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                                                                rowStatus === STATUS_OPTIONS.DRAFT ? 'bg-gray-200 text-gray-700' :
                                                                rowStatus === STATUS_OPTIONS.SUBMITTED ? 'bg-blue-200 text-blue-800' :
                                                                rowStatus === STATUS_OPTIONS.APPROVED ? 'bg-green-200 text-green-800' :
                                                                'bg-red-200 text-red-800'
                                                            }`}>
                                                                {rowStatus}
                                                            </span>
                                                        </td>
                                                        <td className="p-0">
                                                            <input
                                                                type="text"
                                                                disabled={!editable}
                                                                className="w-full p-1 border-none bg-transparent text-[11px] outline-none disabled:text-gray-600"
                                                                value={log.notes}
                                                                onChange={(e) => handleLogChange(logs.findIndex(l => l.date === log.date), 'notes', e.target.value)}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {weekInfo.comments && (
                                                <tr>
                                                    <td colSpan="9" className="p-2 text-xs text-red-700 italic bg-red-50">
                                                        Manager Comment: {weekInfo.comments}
                                                    </td>
                                                </tr>
                                            )}
                                            {activeTab === 'current' && weekInfo.status === STATUS_OPTIONS.DRAFT && (
                                                <tr>
                                                    <td colSpan="9" className="p-2 text-right">
                                                        <button
                                                            onClick={() => handleSubmitWeek(weekKey)}
                                                            className="px-3 py-1 bg-blue-700 text-white text-[10px] font-bold uppercase rounded hover:bg-blue-800"
                                                        >
                                                            Submit Week
                                                        </button>
                                                    </td>
                                                </tr>
                                            )}
                                            {weekInfo.status === STATUS_OPTIONS.APPROVED && (
                                                <tr>
                                                    <td colSpan="9" className="p-2 text-center bg-green-50">
                                                        <LockClosedIcon className="h-4 w-4 inline mr-1 text-green-700" />
                                                        <span className="text-xs font-bold text-green-700">This week is approved and permanently locked.</span>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                            <tfoot className="bg-gray-800 text-white font-bold text-[11px]">
                                <tr>
                                    <td colSpan="3" className="p-1 border-r border-gray-600 text-right uppercase text-[9px] tracking-widest">Total:</td>
                                    <td className="p-1 border-r border-gray-600 text-center">{totals.billable}</td>
                                    <td className="p-1 border-r border-gray-600 text-center text-blue-300">{totals.overtime}</td>
                                    <td className="p-1 border-r border-gray-600 text-center">{(totals.billable + totals.overtime).toFixed(1)}</td>
                                    <td className="p-1 border-r border-gray-600 text-center text-red-300">{totals.sickLeave}</td>
                                    <td colSpan="2" className="p-1"></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                {/* Footer Buttons */}
                <div className="px-3 py-3 bg-gray-100 border-t border-gray-300 flex justify-between items-center">
                    <div className="flex gap-2">
                        <button onClick={downloadExcel} className="flex items-center gap-1 text-[10px] bg-green-700 text-white px-3 py-1.5 rounded hover:bg-green-600 transition font-bold uppercase">
                            <ArrowDownTrayIcon className="h-3.5 w-3.5" /> Export Excel
                        </button>
                        <button onClick={downloadPDF} className="flex items-center gap-1 text-[10px] bg-red-700 text-white px-3 py-1.5 rounded hover:bg-red-600 transition font-bold uppercase">
                            <DocumentArrowDownIcon className="h-3.5 w-3.5" /> Export PDF
                        </button>
                    </div>

                    <div className="flex gap-2 items-center">
                        {sortedWeeks.some(wk =>
                          weekStatuses[wk]?.status !== STATUS_OPTIONS.DRAFT
                        ) && (
                            <button
                                onClick={() => setShowEditRequestModal(true)}
                                className="px-4 py-1.5 bg-orange-600 text-white text-[10px] font-bold uppercase rounded hover:bg-orange-700 flex items-center gap-1"
                            >
                                <PencilIcon className="h-4 w-4" /> Request Edit
                            </button>
                        )}
                        <button onClick={goBack} className="px-4 py-1.5 border border-gray-400 text-gray-600 text-[10px] font-bold uppercase font-poppins hover:bg-white transition rounded">
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {/* Edit Request Modal */}
            {showEditRequestModal && (
                <div className="fixed inset-0 bg-black/50 z-[1100] flex items-center justify-center">
                    <div className="bg-white p-4 rounded shadow-lg max-w-md w-full mx-4">
                        <h3 className="text-sm font-bold mb-3">Request Edit for Locked Week</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-medium mb-1">Reason for Edit <span className="text-red-600">*</span></label>
                                <textarea
                                    value={editReason}
                                    onChange={(e) => setEditReason(e.target.value)}
                                    className="w-full border border-gray-300 p-2 text-xs rounded outline-none"
                                    rows="3"
                                    placeholder="Explain why this week needs to be edited..."
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium mb-1">Additional Comments (optional)</label>
                                <textarea
                                    value={editComments}
                                    onChange={(e) => setEditComments(e.target.value)}
                                    className="w-full border border-gray-300 p-2 text-xs rounded outline-none"
                                    rows="2"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => setShowEditRequestModal(false)} className="px-3 py-1.5 text-xs border border-gray-400 rounded">
                                Cancel
                            </button>
                            <button
                                onClick={handleEditRequest}
                                disabled={!editReason.trim()}
                                className="px-3 py-1.5 text-xs bg-blue-700 text-white rounded disabled:bg-gray-400"
                            >
                                Send Request
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimesheetEntryForm;